<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VPhoneInput } from 'v-phone-input'
import { useI18n } from 'vue-i18n'
import type { VForm } from 'vuetify/components'
import mdiArrowRight from '~icons/mdi/arrow-right'
import mdiCallSplit from '~icons/mdi/call-split'

import swishLogo from '@/assets/swish.png'
import trustlyLogo from '@/assets/trustly.png'
import vismaLogo from '@/assets/visma.png'
import getEnvironment from '@/utils/env'
import { formatCurrency, formatOcr } from '@/utils/format'
import type { PaymentProvider } from './types'

import 'flag-icons/css/flag-icons.min.css'
import 'v-phone-input/styles'

// Provider step (ADA-4432 etapp 5–6) — the heart of the card: it shows WHAT is being paid
// (current amount, not the frozen letter amount) and WHERE the money goes (the OCR), then lets
// the payer pick Swish or bank account.
//
// D10: the legacy structure is kept (status banner, amount block, provider cards with the
// Swish/Trustly artwork) with Vuetify 4 typography — the interaction is not redesigned, there
// being no Figma for this flow.
const props = defineProps<{
  amount: number
  currency?: string
  ocr?: string
  /** Server-derived: hides the payment methods for an already-paid or closed debt. */
  paymentStatus?: 'paid' | 'unpaid' | 'case_closed'
  /** Prefills the Swish step (signed-in payers only — the public flow has no user record). */
  defaultMobileNumber?: string
  /** D6: only rendered once the new_amortization gate has confirmed the debt can be split. */
  canSplitPayment: boolean
  /** The demo payment request is in flight — the confirm button shows it and blocks a second submit. */
  demoLoading?: boolean
}>()

const emit = defineEmits<{
  provider: [{ provider: PaymentProvider; mobileNumber?: string }]
  demo: [number]
  splitPayment: []
}>()

const { t } = useI18n()

// Swish caps a single payment at 150 000 SEK and only handles SEK at all (legacy parity).
const swishAvailable = computed(() => props.currency === 'SEK' && props.amount <= 150000)
// Demo payments exist only where there is no real money. Resolved through the shared environment
// map rather than a second copy of the hostnames — an unrecognised host yields undefined, so the
// safe answer (no demo) is also the default. TEST is included since #915 (demo payments enabled in
// the test environment; ada-api additionally blocks demo payments in production).
const demoAvailable = ['localhost', 'test', 'stage', 'sandbox'].includes(getEnvironment()?.id ?? '')

const showPaymentMethods = computed(() => props.paymentStatus !== 'paid' && props.paymentStatus !== 'case_closed')

const providers = computed(() => {
  const list: { key: PaymentProvider | 'demo'; name: string; logo: string }[] = []
  if (swishAvailable.value) list.push({ key: 'swish', name: t('common.swish'), logo: swishLogo })
  list.push({ key: 'trustly', name: t('common.bank_account'), logo: trustlyLogo })
  if (demoAvailable) list.push({ key: 'demo', name: t('common.demo_payment'), logo: vismaLogo })
  return list
})

// Sub-steps rendered in place of the provider list once a provider needs more input.
const step = ref<'providers' | 'swish' | 'demo'>('providers')

const mobileNumber = ref(props.defaultMobileNumber ?? '')
// The user record can resolve after this page mounted (dialog opened right after app load) —
// take the late prefill, but never overwrite a number the payer already typed themselves.
watch(
  () => props.defaultMobileNumber,
  (value, previous) => {
    if (value == null || value === '') return
    if (mobileNumber.value === '' || mobileNumber.value === previous) mobileNumber.value = value
  }
)
// Swish is Swedish-only, and the api builds Swish's payerAlias as `mobile_number[1:]` — it
// REQUIRES the +46… e164 form, which v-phone-input emits for valid numbers (legacy used its
// PhoneField the same way; a free-text field here let 07…-input through as an invalid alias).
const mobileValid = ref(false)
const swishForm = ref<VForm | null>(null)
// A prefilled number must enable the button without the payer touching the field — but an
// empty field stays untouched so it does not open with a validation error showing.
watch(step, (value) => {
  if (value === 'swish' && mobileNumber.value !== '') void swishForm.value?.validate()
})

const demoAmount = ref('')
const demoValid = computed(() => /^\d+(\.\d+)?$/.test(demoAmount.value.trim()))

function choose(key: PaymentProvider | 'demo') {
  if (key === 'swish') {
    step.value = 'swish'
    return
  }
  if (key === 'demo') {
    step.value = 'demo'
    return
  }
  emit('provider', { provider: 'trustly' })
}

function confirmSwish() {
  if (!mobileValid.value) return
  // The model is already e164 (+46701234567) for a valid number — no separators to strip.
  emit('provider', { provider: 'swish', mobileNumber: mobileNumber.value })
}

function confirmDemo() {
  if (!demoValid.value || props.demoLoading) return
  emit('demo', Number(demoAmount.value))
}
</script>

<template>
  <v-sheet class="d-flex flex-column">
    <v-alert
      v-if="paymentStatus === 'paid'"
      :text="t('common.case_paid')"
      class="ma-4"
      type="success"
      variant="tonal"
    />
    <v-alert
      v-else-if="paymentStatus === 'case_closed'"
      :text="t('common.case_closed')"
      class="ma-4"
      type="info"
      variant="tonal"
    />
    <v-alert
      v-else-if="paymentStatus === 'unpaid'"
      :text="t('common.case_unpaid_text')"
      :title="t('common.case_unpaid')"
      class="ma-4"
      type="warning"
      variant="tonal"
    />

    <template v-if="showPaymentMethods">
      <!-- The amount is the materialized debt and is available synchronously (ADA-4445 removed
           the preliminary interest that used to be fetched and folded in here). -->
      <div class="px-4 pt-2">
        <div class="text-headline-large font-weight-bold tabular-nums">
          {{ formatCurrency(amount, currency, 2) }}
        </div>

        <div
          v-if="ocr != null"
          class="text-body-medium text-medium-emphasis mt-1"
        >
          {{ t('common.ocr') }} {{ formatOcr(ocr) }}
        </div>
      </div>

      <div class="mx-4 mb-8 mt-6">
        <v-fade-transition leave-absolute>
          <div v-show="step === 'providers'">
            <div class="text-body-large font-weight-bold mb-4">{{ t('common.choose_payment_method') }}</div>

            <!-- v-card with only @click renders a plain div (no tabindex/role/key handling in
                 Vuetify 4) — added here so the pick is keyboard-reachable. -->
            <v-card
              v-for="provider in providers"
              :key="provider.key"
              class="d-flex align-center pa-4 mb-4"
              variant="outlined"
              tabindex="0"
              role="button"
              @click="choose(provider.key)"
              @keydown.enter.prevent="choose(provider.key)"
              @keydown.space.prevent="choose(provider.key)"
            >
              <v-avatar
                :image="provider.logo"
                :rounded="0"
                :size="64"
              />
              <div class="px-4 text-body-large">{{ provider.name }}</div>
              <v-spacer />
              <v-icon :icon="mdiArrowRight" />
            </v-card>

            <!-- D6: the shortcut only appears once the new_amortization gate has answered 200 —
                 offering a plan the server will refuse is worse than not offering one. -->
            <v-btn
              v-if="canSplitPayment"
              :prepend-icon="mdiCallSplit"
              variant="text"
              color="primary"
              class="mt-2"
              @click="emit('splitPayment')"
            >
              {{ t('common.split_payment_shortcut') }}
            </v-btn>
          </div>
        </v-fade-transition>

        <v-fade-transition leave-absolute>
          <div v-show="step === 'swish'">
            <div class="text-body-large font-weight-bold mb-4">{{ t('common.confirm_mobile_number') }}</div>

            <!-- SE only: Swish requires a Swedish number, and the single-country picker keeps
                 the model in the +46 form the api's payerAlias derivation expects. -->
            <v-form
              ref="swishForm"
              v-model="mobileValid"
            >
              <!-- variant is a fallthrough attr here — VPhoneInput is not covered by the
                   VTextField defaults (name-keyed) and its plugin options only accept its
                   own props, so outlined must be explicit (as legacy PhoneField had it). -->
              <v-phone-input
                v-model="mobileNumber"
                :country-label="t('common.country')"
                :include-countries="['SE']"
                :invalid-message="t('common.invalid_mobile_number')"
                :label="t('common.mobile_number')"
                country-icon-mode="svg"
                default-country="SE"
                variant="outlined"
              />
            </v-form>

            <v-btn
              :disabled="!mobileValid"
              class="mt-8 mb-4"
              color="primary"
              variant="flat"
              width="100%"
              @click="confirmSwish"
            >
              {{ t('common.pay_with_swish') }}
            </v-btn>
          </div>
        </v-fade-transition>

        <v-fade-transition leave-absolute>
          <div v-show="step === 'demo'">
            <div class="text-body-large font-weight-bold mb-4">{{ t('common.confirm_demo_payment_amount') }}</div>

            <v-text-field
              v-model="demoAmount"
              :label="t('common.amount')"
              :disabled="demoLoading"
              type="number"
            />

            <v-btn
              :disabled="!demoValid || demoLoading"
              :loading="demoLoading"
              class="mt-8 mb-4"
              color="primary"
              variant="flat"
              width="100%"
              @click="confirmDemo"
            >
              {{ t('common.pay') }}
            </v-btn>
          </div>
        </v-fade-transition>
      </div>
    </template>
  </v-sheet>
</template>
